import { useState, useEffect, useRef } from 'react'
import { api } from '@/services/api'
import { FileText, Loader2 } from 'lucide-react'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

// Set up the worker for legacy build
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`

const thumbnailUrlCache = new Map<string, { url: string; expiresAtMs: number }>()

function cacheGet(key: string): string | null {
  const v = thumbnailUrlCache.get(key)
  if (!v) return null
  if (Date.now() >= v.expiresAtMs) {
    thumbnailUrlCache.delete(key)
    return null
  }
  return v.url
}

function cacheSet(key: string, url: string, expiresAtMs?: number) {
  const ttl = expiresAtMs ?? Date.now() + 90 * 24 * 60 * 60 * 1000 // default 90 days
  thumbnailUrlCache.set(key, { url, expiresAtMs: ttl })
}

interface PDFThumbnailProps {
  orgId: string
  fileId: string
  className?: string
  width?: number
  height?: number
}

export function PDFThumbnail({ orgId, fileId, className = '', width = 48, height = 64 }: PDFThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [rendered, setRendered] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  const sizeVariant: 'small' | 'large' = width >= 200 || height >= 200 ? 'large' : 'small'

  useEffect(() => {
    let cancelled = false
    let blobUrl: string | null = null

    const cacheKey = `${orgId}:${fileId}:${sizeVariant}`
    const cached = cacheGet(cacheKey)
    if (cached) {
      setImageUrl(cached)
      setRendered(true)
      setLoading(false)
      return
    }

    const loadCachedThumbnail = async (): Promise<string | null> => {
      const blob = await api.files.thumbnail.getBlob(orgId, fileId, sizeVariant)
      if (!blob) return null
      const url = URL.createObjectURL(blob)
      cacheSet(cacheKey, url)
      return url
    }

    const generateAndUploadThumbnail = async (): Promise<string | null> => {
      if (!canvasRef.current) return null

      const blob = await api.files.downloadBlob(orgId, fileId, { inline: true })
      if (cancelled) return null

      blobUrl = URL.createObjectURL(blob)

      // isEvalSupported:false mitigates CVE-2024-4367 (arbitrary JS exec via crafted PDF)
      // while still on pdfjs 3.x. Remove once upgraded to pdfjs >= 4.2.67.
      const loadingTask = pdfjsLib.getDocument({ url: blobUrl, isEvalSupported: false })
      const pdf = await loadingTask.promise
      if (cancelled) return null

      const page = await pdf.getPage(1)
      if (cancelled) return null

      const viewport = page.getViewport({ scale: 1 })
      const scale = Math.min(width / viewport.width, height / viewport.height)
      const scaledViewport = page.getViewport({ scale })

      const canvas = canvasRef.current
      if (!canvas) return null

      canvas.width = scaledViewport.width
      canvas.height = scaledViewport.height

      const context = canvas.getContext('2d')
      if (!context) return null

      await page
        .render({
          canvasContext: context,
          viewport: scaledViewport,
        })
        .promise

      const webpBlob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/webp', sizeVariant === 'large' ? 0.85 : 0.8)
      )
      if (!webpBlob) return null

      await api.files.thumbnail.upload(orgId, fileId, webpBlob, 'image/webp', sizeVariant)
      if (cancelled) return null

      return await loadCachedThumbnail()
    }

    const renderThumbnail = async () => {
      try {
        setLoading(true)
        setError(false)
        setRendered(false)
        setImageUrl(null)

        const url = (await loadCachedThumbnail()) || (await generateAndUploadThumbnail())
        if (cancelled) return
        if (!url) {
          setError(true)
          setLoading(false)
          return
        }

        cacheSet(cacheKey, url)
        setImageUrl(url)
        setRendered(true)
        setLoading(false)
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to render PDF thumbnail:', e)
          setError(true)
          setLoading(false)
        }
      }
    }

    renderThumbnail()

    return () => {
      cancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [orgId, fileId, width, height, sizeVariant])

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-muted rounded ${className}`} style={{ width, height }}>
        <FileText className="h-6 w-6 text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          className={`rounded border bg-white ${rendered ? 'opacity-100' : 'opacity-0'}`}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          loading="lazy"
        />
      ) : (
      <canvas
        ref={canvasRef}
        className={`rounded border bg-white ${rendered ? 'opacity-100' : 'opacity-0'}`}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      )}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted rounded">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  )
}
